import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Layout, Drawer } from "antd";
import Sidenav from "./consultantSidenav";
import { Header } from "./Header";
import CallBar from "../call/CallBar";
import { useGetConsultantProfileQuery } from "../../redux/rtk/apiRtk";
import { useSocket } from "../../utils/Socket.Provider";
import { CallNotification } from "../../protectedRoutes/consultant";
import { useIsFullscreen } from "../../hooks/IsFullScreen";

const { Header: AntHeader, Content, Sider } = Layout;

function Main({ children }) {
  const audioRef = useRef(new Audio("/callaudio.mp3"));

  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState("right");
  const [sidenavColor, setSidenavColor] = useState("#ff181d");
  const [sidenavType, setSidenavType] = useState("transparent");
  const { socket } = useSocket();
  const params = window.location;
  const [showCallNotification, setShowCallNotification] = useState(false);
  const [callData, setCallData] = useState(null);
  const [missedCalls, setMissedCalls] = useState([]);
  const [showMissedCallNotification, setShowMissedCallNotification] =
    useState(false);
  const [currentMissedCall, setCurrentMissedCall] = useState(null);
  const [fixed, setFixed] = useState(false);
  const [showCallBar, setShowCallBar] = useState(false);
  const { data: ConsultantProfileData, isLoading } =
    useGetConsultantProfileQuery("");
  const isFullScreen = useIsFullscreen();
  const openDrawer = () => setVisible(!visible);
  const handleSidenavType = (type) => setSidenavType(type);
  const handleSidenavColor = (color) => setSidenavColor(color);
  const handleFixedNavbar = (type) => setFixed(type);

  let { pathname } = useLocation();
  pathname = pathname.replace("/", "");

  // Check if current page is a chat page
  const isChatPage =
    pathname === "chats" ||
    window.location.hash.includes("#/chats") ||
    window.location.pathname.includes("/chats");

  useEffect(() => {
    if (pathname === "rtl") {
      setPlacement("left");
    } else {
      setPlacement("right");
    }
  }, [pathname]);

  // Manage body overflow for chat pages
  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    if (isChatPage) {
      // Disable scrolling for chat pages
      bodyEl.style.overflow = "hidden";
      bodyEl.style.height = "100vh";
      htmlEl.style.overflow = "hidden";
      htmlEl.style.height = "100vh";
      bodyEl.classList.add("chat-page-body");
      bodyEl.classList.remove("normal-page-body");
    } else {
      // Enable scrolling for other pages
      bodyEl.style.overflow = "auto";
      bodyEl.style.height = "auto";
      htmlEl.style.overflow = "auto";
      htmlEl.style.height = "auto";
      bodyEl.classList.add("normal-page-body");
      bodyEl.classList.remove("chat-page-body");
    }

    // Cleanup on unmount
    return () => {
      bodyEl.style.overflow = "auto";
      bodyEl.style.height = "auto";
      htmlEl.style.overflow = "auto";
      htmlEl.style.height = "auto";
      bodyEl.classList.remove("chat-page-body", "normal-page-body");
    };
  }, [isChatPage]);

  useEffect(() => {
    // Join room with consultant ID
    if (ConsultantProfileData?._id && socket) {
      socket.emit("join-call-room", ConsultantProfileData._id);
    }

    // Handle incoming call event
    const handleIncomingCall = async (data) => {
      if (showCallBar) {
        return;
      }
      if (audioRef.current && audioRef.current.paused) {
        try {
          audioRef.current.loop = true;
          await audioRef?.current?.play();
        } catch (error) {
          console.error("Error playing audio:", error);
        }
      }
      setCallData(data);
      setShowCallNotification(true);
    };

    // Handle missed call event
    const handleCallCanceled = (data) => {
      console.log("Call canceled notification received:", data);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setCallData(null);
      setShowCallNotification(false);
    };

    if (socket) {
      // socket.on("end-call", handleEndCall);
      socket.on("incoming-call", handleIncomingCall);
      // socket.on("missed-call", handleMissedCall);
      socket.on("call-cancelled", handleCallCanceled);

      // call-cancelled
      socket.on("consultant-busy", (data) => {
        console.log("Consultant busy notification received:", data);
      });
    }

    return () => {
      if (socket) {
        socket.off("incoming-call", handleIncomingCall);
        // socket.off("end-call", handleEndCall);
        // socket.off("missed-call", handleMissedCall);
        socket.off("consultant-busy");
        socket.off("call-cancelled");
      }
    };
  }, [
    socket,
    ConsultantProfileData,
    params.hash,
    isLoading,
    missedCalls.length,
    showMissedCallNotification,
    showCallNotification,
  ]);

  // Handle accepting call
  const handleAcceptCall = () => {
    if (callData && socket && ConsultantProfileData?._id) {
      // Request microphone permissions before accepting call
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          // Permissions granted, accept the call
          socket.emit("accept-call", {
            toUser: callData.fromUser,
            fromUser: ConsultantProfileData._id,
            perMinuteCoin: 0,
            consultantid: ConsultantProfileData._id,
            user: callData.fromUser,
            meetingId: callData.meetingId,
          });
          console.log("shwoing ...");

          setShowCallNotification(false);

          setShowCallBar(true);
          document.documentElement.requestFullscreen();
        })
        .catch((error) => {
          // Handle permission denied
          console.error("Microphone permission denied:", error);
          alert(
            "Microphone access is required to accept calls. Please allow microphone access and try again."
          );
        });
    }
  };

  // Handle call bar end call
  const handleCallBarEndCall = () => {
    console.log({ isFullScreen });

    document.exitFullscreen();

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setShowCallBar(false);

    setCallData(null);
    window.location.reload();
  };
  // Handle rejecting call
  const handleRejectCall = () => {
    if (callData && socket && ConsultantProfileData?._id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      socket.emit("cancel-call", {
        toUser: callData.fromUser,
        fromUser: ConsultantProfileData._id,
        meetingId: callData.meetingId,
      });
      // Hide notification and clear call data
      setShowCallNotification(false);
    }
  };

  // Handle dismissing a missed call notification
  const handleDismissMissedCall = () => {
    setShowMissedCallNotification(false);
    setCurrentMissedCall(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <>
      {/* Call Bar */}

      <CallBar
        isVisible={showCallBar}
        callData={callData}
        onEndCall={handleCallBarEndCall}
      />

      {showCallNotification && callData && (
        <CallNotification
          callerName={callData.name}
          callerImage={callData.profilePic}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      <Layout
        className={`layout-dashboard h-screen ${
          isChatPage ? "overflow-hidden" : "overflow-auto"
        } ${pathname === "profile" ? "layout-profile" : ""} ${
          pathname === "rtl" ? "layout-dashboard-rtl" : ""
        }`}
      >
        <Drawer
          title={false}
          placement={placement === "right" ? "left" : "right"}
          closable={false}
          onClose={() => setVisible(false)}
          visible={visible}
          key={placement === "right" ? "left" : "right"}
          width={250}
          // className={`drawer-sidebar z-[1001] ${pathname === "rtl" ? "drawer-sidebar-rtl" : ""
          //   }`}
        >
          <Layout
          // className={`layout-dashboard ${pathname === "rtl" ? "layout-dashboard-rtl" : ""
          //   }`}
          >
            <Sider
              trigger={null}
              width={200}
              theme="light"
              className={`sider-primary ant-layout-sider-primary ${
                sidenavType === "#fff" ? "active-route" : ""
              }`}
              style={{ background: sidenavType }}
            >
              <Sidenav color={sidenavColor} />
            </Sider>
          </Layout>
        </Drawer>
        <Sider
          breakpoint="lg"
          collapsedWidth="0"
          onCollapse={(collapsed, type) => {
            console.log(collapsed, type);
          }}
          trigger={null}
          width={250}
          theme="light"
          className={`sider-primary ant-layout-sider-primary fixed left-0 top-0 h-screen z-[99] bg-white hidden lg:block ${
            sidenavType === "#fff" ? "active-route" : ""
          }`}
        >
          <Sidenav color={sidenavColor} />
        </Sider>
        <Layout className="h-screen flex flex-col">
          <AntHeader className=" z-[100] bg-white border-b border-gray-200 px-0 h-16 rounded-2xl ">
            <Header
              onPress={openDrawer}
              name={pathname}
              subName={pathname}
              handleSidenavColor={handleSidenavColor}
              handleSidenavType={handleSidenavType}
              handleFixedNavbar={handleFixedNavbar}
            />
          </AntHeader>
          <div className="fixed top-0 right-0 left-64" />
          <div
            className={`
          ${
            isChatPage
              ? " h-[calc(100vh-4rem)] overflow-hidden  flex flex-col p-0"
              : " h-full"
          }`}
          >
            {children}
          </div>
        </Layout>
      </Layout>
    </>
  );
}

export default Main;
